import { ArrowRight, CheckCircle2 } from "lucide-react";
import { IMAGES } from "@/data/products";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd } from "@/components/site/SEO";
import { Reveal, MaskedLines, Eyebrow } from "@/components/site/Reveal";
import { CTASection } from "@/components/site/CTASection";

const shutterBenefits = [
  {
    title: "Privacy without darkness",
    text: "Tilt the louvres and the street can't see in — while daylight still fills the room. Few window coverings balance the two so well.",
  },
  {
    title: "Beautiful light control",
    text: "From wide open to fully closed, shutters give you fine, silent control over brightness and glare at any time of day.",
  },
  {
    title: "A finish that elevates a room",
    text: "Shutters read as part of the architecture. They suit period Birmingham terraces and new builds alike, and never go out of style.",
  },
  {
    title: "Built to last",
    text: "Solid, made-to-measure construction that wipes clean and keeps its looks for years — a genuine long-term investment in your home.",
  },
  {
    title: "A layer of insulation",
    text: "Shutters add a substantial barrier at the window, helping rooms feel warmer in winter and shaded in summer.",
  },
  {
    title: "Made for awkward windows",
    text: "Bays, arches, French doors and unusual shapes are where made-to-measure shutters really come into their own.",
  },
];

const styles = [
  {
    title: "Full height",
    text: "Clean, unbroken panels covering the whole window — the most popular choice for bedrooms and street-facing rooms.",
  },
  {
    title: "Tier on tier",
    text: "Independent top and bottom panels, so you can open the top for light while the bottom stays closed for privacy.",
  },
  {
    title: "Café style",
    text: "Panels on the lower half only — privacy at street level with completely open glass above. Lovely in kitchens and bay windows.",
  },
];

export default function Shutters() {
  const { openForm } = useLeadForm();
  return (
    <>
      <SEO
        title="Made-to-Measure Shutters Birmingham | Fitted Plantation Shutters"
        description="Premium made-to-measure shutters in Birmingham — privacy, light control and timeless style, professionally measured and fitted. Book a free shutter consultation."
        path="/shutters"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Shutters", path: "/shutters" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Made-to-measure shutters",
            provider: { "@type": "LocalBusiness", name: "My Blinds Company Ltd" },
            areaServed: "Birmingham, UK",
          },
        ]}
      />

      {/* HERO */}
      <section className="relative bg-navy-950 text-white overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Eyebrow className="text-white/50 mb-5">Shutters</Eyebrow>
            <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
              <MaskedLines lines={["Shutters that belong", "to the house,"]} />
              <MaskedLines lines={["not just the window"]} className="italic text-white/70" />
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/70 max-w-xl">
              Made-to-measure shutters bring privacy, light control and quiet
              permanence to a room. We measure every angle of your windows and
              fit each panel ourselves.
            </p>
            <button
              type="button"
              data-testid="shutters-consultation-btn"
              onClick={() => openForm({ source: "shutters_hero", productInterest: "Shutters" })}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-navy-900 shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            >
              Book a Free Shutter Consultation <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.2}>
              <div className="overflow-hidden rounded-[2rem] aspect-[4/3] shadow-2xl">
                <img
                  src={IMAGES.shutters}
                  alt="Made-to-measure white plantation shutters fitted in a bright living room"
                  className="h-full w-full object-cover"
                  fetchpriority="high"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 sm:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Eyebrow>Why Shutters</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              The window covering you buy once
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shutterBenefits.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-warm-200 bg-white p-7 shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <h3 className="font-sans text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STYLES */}
      <section className="py-16 sm:py-24 bg-warm-100/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] aspect-[4/5] shadow-xl">
              <img
                src={IMAGES.shuttersSunroom}
                alt="Sunroom with made-to-measure shutters controlling the afternoon light"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow>Configurations</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Shaped around your windows
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Every shutter we fit is made to measure. During your free
              consultation we'll advise which configuration suits each window —
              including bays, doors and unusual shapes.
            </p>
            <div className="mt-8 space-y-5">
              {styles.map((s) => (
                <div key={s.title} className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 mt-1 shrink-0 text-navy-800" />
                  <div>
                    <h3 className="font-sans text-base font-semibold text-slate-900">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600 mt-1">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MEASURING */}
      <section className="py-16 sm:py-24 bg-navy-950 grain text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">
              Measured to the millimetre, fitted to last
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              Shutters forgive nothing — a window that's even slightly out of
              square shows. That's why every shutter order begins with careful
              professional measuring, and ends with our own fitters installing
              and adjusting each panel until it sits perfectly.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection
        title="See what shutters would do for your home"
        text="Book a free shutter consultation — we'll measure up, talk through styles and finishes, and leave you a clear quotation."
        primaryLabel="Book a Free Shutter Consultation"
        source="shutters_page_cta"
      />
    </>
  );
}
